import { Controller } from '@hotwired/stimulus'

// Bible Reader controller
//
// Provides:
//   - Infinite scroll: loads the next chapter (or previous) via Turbo Frame
//     when sentinel elements scroll into the viewport.
//   - Translation switching: reloads all visible chapters when the user
//     picks a different translation, without a full page reload.
//   - Placement visibility toggle: shows / hides all tool-placement badges
//     across the rendered chapters.
//   - Scroll tracking: updates the URL and passage indicator as the user
//     scrolls through chapters via history.replaceState.
//
// Expected DOM structure:
//
//   [data-controller="bible-reader"
//    data-bible-reader-chapter-url-value="..."
//    data-bible-reader-book-id-value="43"
//    data-bible-reader-chapter-count-value="21"
//    data-bible-reader-translation-value="NIV"
//    data-bible-reader-show-placements-value="true"]
//
//     <!-- Rendered chapters are appended here -->
//     [data-bible-reader-target="chaptersContainer"]
//
//     <!-- Sentinel at the bottom triggers next-chapter loading -->
//     [data-bible-reader-target="bottomSentinel"]
export default class extends Controller {
  static targets = [
    'annotationsToggle',
    'bottomSentinel',
    'chaptersContainer',
    'passageIndicator',
    'placementBadge',
    'placementPanel',
    'placementPanelContent',
    'topSentinel',
    'translationButton'
  ]

  static values = {
    chapterUrl: String,
    placementUrl: String,
    bookId: Number,
    chapterCount: Number,
    translation: String,
    showPlacements: Boolean,
    // Track which chapters are rendered to avoid double-loading
    lowestChapter: Number,
    highestChapter: Number
  }

  connect () {
    this.loading = false
    this.observer = new IntersectionObserver(
      this.handleIntersection.bind(this),
      { rootMargin: '200px' }
    )

    if (this.hasBottomSentinelTarget) {
      this.observer.observe(this.bottomSentinelTarget)
    }
    if (this.hasTopSentinelTarget) {
      this.observer.observe(this.topSentinelTarget)
    }

    // Scroll tracking for URL + passage indicator
    this._scrollHandler = this.onScroll.bind(this)
    window.addEventListener('scroll', this._scrollHandler, { passive: true })

    // Position the sticky bar and set content padding.
    // Padding only needs updating on connect and resize (not every scroll).
    this._resizeHandler = this.setContentPadding.bind(this)
    window.addEventListener('resize', this._resizeHandler, { passive: true })
    this.setStickyBarTop()
    // Padding depends on bar.offsetHeight which needs a frame to measure
    requestAnimationFrame(() => this.setContentPadding())

    this.applyPlacementVisibility()
  }

  disconnect () {
    this.observer?.disconnect()
    window.removeEventListener('scroll', this._scrollHandler)
    window.removeEventListener('resize', this._resizeHandler)
  }

  // -------------------------------------------------------------------------
  // Sticky bar positioning — two separate concerns:
  //   setStickyBarTop   — cheap, runs on every scroll frame
  //   setContentPadding — measures DOM, runs only on connect and resize
  // -------------------------------------------------------------------------

  setStickyBarTop () {
    const header = document.getElementById('site-header')
    const bar = this.element.querySelector('.bible-reader-sticky-bar')
    if (!bar) return
    const headerHeight = header ? header.offsetHeight : 0
    const visibleHeader = Math.max(0, headerHeight - window.scrollY)
    bar.style.top = `${visibleHeader}px`
  }

  setContentPadding () {
    const bar = this.element.querySelector('.bible-reader-sticky-bar')
    const content = this.element.querySelector('.bible-reader-content')
    if (!bar || !content) return
    content.style.paddingTop = `${32 + bar.offsetHeight + 8}px`
  }

  // Re-register sentinels with the observer after DOM changes so the
  // IntersectionObserver picks up their new positions.
  reattachObservers () {
    if (this.hasBottomSentinelTarget) {
      this.observer.unobserve(this.bottomSentinelTarget)
      this.observer.observe(this.bottomSentinelTarget)
    }
    if (this.hasTopSentinelTarget) {
      this.observer.unobserve(this.topSentinelTarget)
      this.observer.observe(this.topSentinelTarget)
    }
  }

  // -------------------------------------------------------------------------
  // IntersectionObserver callback
  // -------------------------------------------------------------------------

  handleIntersection (entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting || this.loading) return

      if (entry.target === this.bottomSentinelTarget) {
        this.loadNextChapter()
      } else if (entry.target === this.topSentinelTarget) {
        this.loadPreviousChapter()
      }
    })
  }

  // -------------------------------------------------------------------------
  // Chapter loading
  // -------------------------------------------------------------------------

  loadNextChapter () {
    const next = this.highestChapterValue + 1
    if (next > this.chapterCountValue) return

    this.loadChapter(next, 'append')
  }

  loadPreviousChapter () {
    const prev = this.lowestChapterValue - 1
    if (prev < 1) return

    this.loadChapter(prev, 'prepend')
  }

  async loadChapter (chapter, position) {
    if (this.loading) return
    this.loading = true

    const url = this.buildChapterUrl(chapter)
    try {
      const response = await fetch(url, {
        headers: { Accept: 'text/html' }
      })

      if (!response.ok) {
        console.error('BibleReader: chapter fetch failed', response.status)
        return
      }

      const html = await response.text()
      const container = this.chaptersContainerTarget

      if (position === 'append') {
        container.insertAdjacentHTML('beforeend', html)
        this.highestChapterValue = chapter
      } else {
        // Prepend without losing scroll position
        const previousScrollHeight = document.documentElement.scrollHeight
        container.insertAdjacentHTML('afterbegin', html)
        const delta = document.documentElement.scrollHeight - previousScrollHeight
        window.scrollBy(0, delta)
        this.lowestChapterValue = chapter
      }

      this.applyPlacementVisibility()
      this.updateCurrentChapter()
      this.reattachObservers()
    } catch (e) {
      console.error('BibleReader: error loading chapter', e)
    } finally {
      this.loading = false
    }
  }

  buildChapterUrl (chapter) {
    const url = new URL(this.chapterUrlValue, window.location.origin)
    url.searchParams.set('book_id', this.bookIdValue)
    url.searchParams.set('chapter', chapter)
    url.searchParams.set('translation', this.translationValue)
    url.searchParams.set('show_placements', this.showPlacementsValue)
    return url.toString()
  }

  // -------------------------------------------------------------------------
  // Scroll tracking — URL + passage indicator
  // -------------------------------------------------------------------------

  onScroll () {
    // Throttle: only run once per animation frame
    if (this._scrollRaf) return
    this._scrollRaf = requestAnimationFrame(() => {
      this._scrollRaf = null
      this.setStickyBarTop()
      this.updateCurrentChapter()
    })
  }

  // Find the chapter section whose top edge is closest to (but not past)
  // the middle of the viewport. Falls back to the first rendered chapter.
  updateCurrentChapter () {
    const sections = Array.from(
      this.chaptersContainerTarget.querySelectorAll('section[data-chapter]')
    )
    if (sections.length === 0) return

    const midpoint = window.scrollY + window.innerHeight / 2
    let best = sections[0]
    for (const section of sections) {
      if (section.offsetTop <= midpoint) {
        best = section
      } else {
        break
      }
    }

    const chapter = parseInt(best.dataset.chapter, 10)
    const bookName = best.dataset.bookName || ''

    this.updatePassageIndicator(bookName, chapter)
    this.updateUrl(chapter)
  }

  updatePassageIndicator (bookName, chapter) {
    if (!this.hasPassageIndicatorTarget) return
    this.passageIndicatorTarget.textContent =
      `${bookName} ${chapter} \u2014 ${this.translationValue}`
  }

  updateUrl (chapter) {
    const url = new URL(window.location.href)
    url.searchParams.set('book_id', this.bookIdValue)
    url.searchParams.set('chapter', chapter)
    url.searchParams.set('translation', this.translationValue)
    history.replaceState(null, '', url.toString())
  }

  // -------------------------------------------------------------------------
  // Translation switching
  // -------------------------------------------------------------------------

  switchTranslation (event) {
    const button = event.currentTarget
    const translation = button.dataset.translation
    if (!translation || translation === this.translationValue) return

    this.translationValue = translation
    this.updateTranslationButtons(translation)
    this.reloadAllChapters()
    // Refresh the indicator text so the translation label updates immediately
    this.updateCurrentChapter()
  }

  updateTranslationButtons (activeTranslation) {
    if (!this.hasTranslationButtonTarget) return
    this.translationButtonTargets.forEach((btn) => {
      const isActive = btn.dataset.translation === activeTranslation
      btn.classList.toggle('bg-custom-green', isActive)
      btn.classList.toggle('text-white', isActive)
      btn.classList.toggle('shadow', isActive)
      btn.classList.toggle('bg-custom-green-light', !isActive)
      btn.classList.toggle('text-custom-green', !isActive)
    })
  }

  async reloadAllChapters () {
    const container = this.chaptersContainerTarget

    // Collect all currently visible chapter numbers from rendered chapter sections
    const rendered = Array.from(
      container.querySelectorAll('[data-chapter]')
    ).map((el) => parseInt(el.dataset.chapter, 10))

    if (rendered.length === 0) return

    // Replace each chapter's HTML in place
    container.innerHTML = ''
    this.loading = true

    try {
      for (const chapter of rendered) {
        const url = this.buildChapterUrl(chapter)
        const response = await fetch(url, { headers: { Accept: 'text/html' } })
        if (response.ok) {
          const html = await response.text()
          container.insertAdjacentHTML('beforeend', html)
        }
      }
      this.applyPlacementVisibility()
    } finally {
      this.loading = false
    }
  }

  // -------------------------------------------------------------------------
  // Placement visibility toggle
  // -------------------------------------------------------------------------

  togglePlacements () {
    this.showPlacementsValue = !this.showPlacementsValue
    this.applyPlacementVisibility()
  }

  applyPlacementVisibility () {
    const show = this.showPlacementsValue
    // Target all placement badge containers rendered in chapters
    this.element
      .querySelectorAll('[data-bible-reader-placement]')
      .forEach((el) => {
        el.style.display = show ? '' : 'none'
      })
    // Reflect toggle state on the button
    if (this.hasAnnotationsToggleTarget) {
      this.annotationsToggleTarget.classList.toggle('bg-custom-green', show)
      this.annotationsToggleTarget.classList.toggle('text-white', show)
      this.annotationsToggleTarget.classList.toggle('border-custom-green', show)
      this.annotationsToggleTarget.classList.toggle('text-gray-600', !show)
      this.annotationsToggleTarget.classList.toggle('border-gray-300', !show)
    }
  }

  // Called by Stimulus when showPlacementsValue changes
  showPlacementsValueChanged () {
    this.applyPlacementVisibility()
  }

  // -------------------------------------------------------------------------
  // Annotation detail panel (slide-up)
  // -------------------------------------------------------------------------

  async openPlacement (event) {
    const placementId = event.currentTarget.dataset.placementId
    if (!placementId) return

    const url = new URL(this.placementUrlValue, window.location.origin)
    url.searchParams.set('placement_id', placementId)
    url.searchParams.set('translation', this.translationValue)

    try {
      const response = await fetch(url.toString(), { headers: { Accept: 'text/html' } })
      if (!response.ok) return

      const html = await response.text()
      this.placementPanelContentTarget.innerHTML = html
      this.showPlacementPanel()
    } catch (e) {
      console.error('BibleReader: error loading placement detail', e)
    }
  }

  showPlacementPanel () {
    const panel = this.placementPanelTarget
    panel.classList.remove('hidden')
    document.body.style.overflow = 'hidden'
    requestAnimationFrame(() => {
      panel.classList.add('translate-y-0')
      panel.classList.remove('translate-y-full')
    })
  }

  closePlacementPanel () {
    const panel = this.placementPanelTarget
    panel.classList.add('translate-y-full')
    panel.classList.remove('translate-y-0')
    document.body.style.overflow = ''
    setTimeout(() => {
      panel.classList.add('hidden')
      this.placementPanelContentTarget.innerHTML = ''
    }, 250)
  }
}
